<template>
  <BaseDialog
    centered
    :contentClass="hasColorChoice ? 'edit-category edit-category--document-keyword' : 'edit-category'"
    ref="dialog"
    persistent
    show-actions
  >
    <template slot="title">{{ $t('page.admin-keywords.edit-category') }}</template>
    <template slot="content">
      <form @click.prevent>
        <div>
          <FormInput
            :label="$t('page.admin-keywords.category-name')"
            has-gray-background
            name="name"
            type="name"
            isRequired
            ref="categoryName"
            v-model="name"
            v-validate="'required'"
            @keydown.enter="onSubmit"
          />
        </div>
        <FormColorPicker
          v-if="hasColorChoice"
          v-model="categoryColor"
          :isRequired="true"
          :label="$t('page.admin-opportunity-management.add-dialog.color')"
        ></FormColorPicker>
        <div v-if="!hasColorChoice" class="preferences__locale">
          <div class="preferences__label">
            <FormLabel>{{ $t('page.admin-user-profile.language') }}</FormLabel>
          </div>
          <div class="preferences__dropdown">
            <LocaleProvider ref="localeProvider">
              <template slot-scope="{ locales }">
                <BaseSelect
                  name="locale"
                  :value="locale"
                  :items="locales"
                  item-text="name"
                  item-value="locale"
                  :hint="$t('page.admin-user-profile.language-hint')"
                  @change="onLocaleChange"
                />
              </template>
            </LocaleProvider>
          </div>
        </div>
        <div class="edit-category__actions">
          <FormButton is-outlined class="edit-category__cancel" @click.native="cancelClick">{{
            $t('actions.cancel')
          }}</FormButton>
          <FormButton @click.native="onSubmit" type="submit" :disabled="isFormInvalid">
            <span>{{ $t('dialog.filters.advanced.apply') }}</span>
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import FormButton from '@/components/form/FormButton';
import FormInput from '@/components/form/FormInput.vue';
import FormLabel from '@/components/form/FormLabel.vue';
import LocaleProvider from '@/components/locale/LocaleProvider';
import BaseSelect from '@/components/base/BaseSelect';
import FormColorPicker from '../form/FormColorPicker.vue';

import { mapActions } from 'vuex';

export default {
  name: 'EditCategoryDialog',
  components: {
    BaseDialog,
    FormButton,
    FormInput,
    FormLabel,
    LocaleProvider,
    BaseSelect,
    FormColorPicker,
  },
  data() {
    return {
      name: '',
      locale: 'fr-CA',
      category: null,
      categoryColor: '',
      initialName: '',
      initialLocale: '',
      initialColor: '',
    };
  },
  props: {
    hasColorChoice: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isFormInvalid() {
      return !!!this.name || !this.isDirty;
    },
    isDirty() {
      const nameChanged = this.name !== this.initialName;
      const colorChanged = this.hasColorChoice && this.categoryColor !== this.initialColor;
      const localeChanged = !this.hasColorChoice && this.locale !== this.initialLocale;
      return nameChanged || colorChanged || localeChanged;
    },
  },
  methods: {
    ...mapActions('keywords', ['editCategory']),
    ...mapActions('documentKeywords', ['updateDocumentCategory']),
    openDialog(category) {
      this.name = category.name;
      this.locale = category.language + '-CA';
      this.category = category;
      this.categoryColor = category.colorCode;

      this.initialName = category.name;
      this.initialLocale = category.language + '-CA';
      this.initialColor = category.colorCode;

      this.$refs.dialog.openModal();

      this.$nextTick(() => {
        const refInput = this.$refs.categoryName.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });
    },
    closeDialog() {
      this.name = '';
      this.locale = 'fr-CA';
      this.$refs.dialog.isModalOpen = false;
    },
    onLocaleChange(locale) {
      this.locale = locale;
    },
    onSubmit() {
      if (this.hasColorChoice) {
        this.updateDocumentCategory({ categoryId: this.category.id, name: this.name, colorCode: this.categoryColor })
          .then(() => {
            this.$toast.success(this.$t('page.admin-keywords.edit-category-success'));
            this.$emit('action-succeeded');
          })
          .catch((error) => this.$toast.error(error));
      } else {
        const category = {
          id: this.category.id,
          tenantId: this.category.tenantId,
          language: this.locale.split('-')[0],
          name: this.name,
        };
        this.editCategory({ categoryId: category.id, category })
          .then(() => {
            this.$toast.success(this.$t('page.admin-keywords.edit-category-success'));
            this.$emit('action-succeeded');
          })
          .catch((error) => this.$toast.error(error));
      }

      this.closeDialog();
    },
    cancelClick() {
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss">
.base-dialog__edit-category {
  height: auto;
  width: 550px;
  position: relative;

  .base-dialog__content {
    padding: 32px;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}

.edit-category {
  &__actions {
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
  }

  &__cancel {
    margin-right: 12px;
  }

  &__action {
    text-transform: lowercase;
    font-weight: bold;
  }
}
.edit-category--document-keyword {
  .form-input {
    padding-bottom: 0;
    &__input {
      margin-bottom: 16px;
    }
  }
  .form-colorpicker {
    padding-top: 0;
    .form-label {
      margin-bottom: 10px;
    }
  }
}
</style>
